import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const PRESETS = {
  google: { smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_security: "starttls" },
  zoho: { smtp_host: "smtp.zoho.in", smtp_port: 465, smtp_security: "ssl" },
  m365: { smtp_host: "smtp.office365.com", smtp_port: 587, smtp_security: "starttls" },
  hostinger: { smtp_host: "smtp.hostinger.com", smtp_port: 465, smtp_security: "ssl" },
};
const EMPTY = { smtp_enabled: true, smtp_host: "", smtp_port: 587, smtp_security: "starttls", smtp_user: "", smtp_pwd: "", smtp_from_email: "", smtp_from_name: "", smtp_reply_to: "" };

export default function SmtpSettingsPanel() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    const d = (await api.get("/email/smtp")).data;
    setF({ ...EMPTY, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null)), smtp_pwd: d.smtp_pwd_masked || "" });
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy("save");
    try { await api.put("/email/smtp", { ...f, smtp_port: Number(f.smtp_port) }); toast.success("Company email settings saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const r = (await api.post("/email/smtp/test", { ...f, smtp_port: Number(f.smtp_port) })).data;
      if (r.ok) toast.success(`Test email sent to ${r.to}`); else toast.error(r.error);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); } finally { setBusy(""); }
  };

  return (
    <Card className="p-4 rounded-sm space-y-4" data-testid="smtp-settings-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium flex items-center gap-2"><Mail className="w-4 h-4" /> Company email (your own mail server)</div>
          <p className="text-xs text-muted-foreground">Invoices, reminders, reports and digests go out from your own address. If this is off, emails use Zenith 360's built-in sender.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">Use my mail server
          <Switch checked={!!f.smtp_enabled} onCheckedChange={(v) => setF({ ...f, smtp_enabled: v })} data-testid="smtp-enabled-switch" />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap text-xs">
        <span className="text-muted-foreground self-center">Quick fill:</span>
        {[["google", "Google Workspace"], ["zoho", "Zoho Mail"], ["m365", "Microsoft 365"], ["hostinger", "Hostinger"]].map(([k, l]) => (
          <Button key={k} size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setF({ ...f, ...PRESETS[k] })} data-testid={`smtp-preset-${k}`}>{l}</Button>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>SMTP host</Label><Input value={f.smtp_host} onChange={set("smtp_host")} className="rounded-sm mt-1 font-mono" data-testid="smtp-host" /></div>
        <div><Label>Port</Label><Input type="number" value={f.smtp_port} onChange={set("smtp_port")} className="rounded-sm mt-1" data-testid="smtp-port" /></div>
        <div><Label>Security</Label>
          <select value={f.smtp_security} onChange={set("smtp_security")} className="w-full border rounded-sm mt-1 h-9 px-2 bg-background text-sm" data-testid="smtp-security">
            <option value="starttls">STARTTLS (587)</option><option value="ssl">SSL/TLS (465)</option>
          </select>
        </div>
        <div><Label>Username</Label><Input value={f.smtp_user} onChange={set("smtp_user")} className="rounded-sm mt-1" data-testid="smtp-user" /></div>
        <div><Label>Password / app password</Label><Input type="password" value={f.smtp_pwd} onChange={set("smtp_pwd")} placeholder={f.smtp_pwd_set ? "Leave as is to keep the saved one" : "Not saved yet — enter mailbox password"} className="rounded-sm mt-1" data-testid="smtp-password" /></div>
        <div><Label>Sender email</Label><Input value={f.smtp_from_email} onChange={set("smtp_from_email")} placeholder="billing@yourcompany.in" className="rounded-sm mt-1" data-testid="smtp-from-email" /></div>
        <div><Label>Sender name</Label><Input value={f.smtp_from_name} onChange={set("smtp_from_name")} placeholder="Your Company Accounts" className="rounded-sm mt-1" data-testid="smtp-from-name" /></div>
        <div><Label>Reply-to (optional)</Label><Input value={f.smtp_reply_to} onChange={set("smtp_reply_to")} className="rounded-sm mt-1" data-testid="smtp-reply-to" /></div>
      </div>
      <p className="text-[11px] text-muted-foreground">Google Workspace and Microsoft 365 need an app password (or SMTP AUTH turned on). Your mail provider already handles SPF/DKIM for your domain.</p>
      <div className="text-[11px]" data-testid="smtp-pwd-state">{f.smtp_pwd_set ? <span className="text-emerald-600">Password saved</span> : <span className="text-amber-600">No password saved yet — enter it and click Save</span>}</div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={!!busy || !f.smtp_host || !f.smtp_from_email} className="rounded-sm bg-accent text-white hover:bg-accent/90" data-testid="smtp-save-btn">{busy === "save" ? "Saving…" : "Save"}</Button>
        <Button variant="outline" onClick={test} disabled={!!busy} className="rounded-sm" data-testid="smtp-test-btn">{busy === "test" ? "Sending…" : "Send test email to me"}</Button>
      </div>
    </Card>
  );
}
